import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Sparkles, X } from 'lucide-react';

export type SpotlightStep = {
  target: string;
  title: string;
  description: string;
  detail?: string;
};

type SpotlightTourProps = {
  label: string;
  steps: SpotlightStep[];
  stepIndex: number;
  onStepChange: (index: number) => void;
  onFinish: () => void;
  scrollBlock?: ScrollLogicalPosition;
};

type Highlight = { top: number; left: number; width: number; height: number };

export const SpotlightTour = ({ label, steps, stepIndex, onStepChange, onFinish, scrollBlock = 'nearest' }: SpotlightTourProps) => {
  const [highlight, setHighlight] = useState<Highlight | null>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLElement>(null);
  const [measuredCardHeight, setMeasuredCardHeight] = useState(310);
  const step = steps[stepIndex];

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const measure = () => setMeasuredCardHeight(card.getBoundingClientRect().height);
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(card);
    return () => observer?.disconnect();
  }, [stepIndex]);

  useEffect(() => {
    const target = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
    if (!target) {
      setHighlight(null);
      return;
    }

    target.scrollIntoView({ block: scrollBlock, inline: 'nearest', behavior: 'smooth' });
    const updateHighlight = () => {
      const bounds = target.getBoundingClientRect();
      const left = Math.max(8, bounds.left - 6);
      const top = Math.max(8, bounds.top - 6);
      setHighlight({
        top,
        left,
        width: Math.max(0, Math.min(window.innerWidth - left - 8, bounds.width + 12)),
        height: Math.max(0, Math.min(window.innerHeight - top - 8, bounds.height + 12))
      });
    };
    updateHighlight();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHighlight) : null;
    observer?.observe(target);
    window.addEventListener('resize', updateHighlight);
    document.addEventListener('scroll', updateHighlight, true);
    const timer = window.setTimeout(updateHighlight, 350);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateHighlight);
      document.removeEventListener('scroll', updateHighlight, true);
      window.clearTimeout(timer);
    };
  }, [step.target, scrollBlock]);

  useEffect(() => {
    nextButtonRef.current?.focus();
  }, [stepIndex]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onFinish();
      if (event.key === 'ArrowRight') onStepChange(Math.min(steps.length - 1, stepIndex + 1));
      if (event.key === 'ArrowLeft') onStepChange(Math.max(0, stepIndex - 1));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onFinish, onStepChange, stepIndex, steps.length]);

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const cardWidth = Math.min(380, viewportWidth - 24);
  const cardHeight = Math.min(measuredCardHeight, viewportHeight - 24);
  const centeredLeft = highlight
    ? Math.max(12, Math.min(viewportWidth - cardWidth - 12, highlight.left + highlight.width / 2 - cardWidth / 2))
    : Math.max(12, (viewportWidth - cardWidth) / 2);
  const canFitBelow = highlight && highlight.top + highlight.height + cardHeight + 24 < viewportHeight;
  const canFitAbove = highlight && highlight.top - cardHeight - 16 > 8;
  const canFitLeft = highlight && highlight.left > cardWidth + 24;
  const canFitRight = highlight && viewportWidth - highlight.left - highlight.width > cardWidth + 24;
  const useSide = highlight && !canFitAbove && !canFitBelow && (canFitLeft || canFitRight);
  const cardLeft = useSide
    ? canFitLeft ? highlight.left - cardWidth - 14 : highlight.left + highlight.width + 14
    : centeredLeft;
  const cardTop = useSide
    ? Math.max(12, Math.min(viewportHeight - cardHeight - 12, highlight.top + highlight.height / 2 - cardHeight / 2))
    : canFitBelow
    ? highlight.top + highlight.height + 14
    : canFitAbove
      ? highlight.top - cardHeight - 14
      : Math.max(12, (viewportHeight - cardHeight) / 2);

  return createPortal(
    <div className="print:hidden" aria-label={label}>
      <div className={`fixed inset-0 z-[300] ${highlight ? 'bg-transparent' : 'bg-slate-950/75'}`} />
      {highlight && (
        <div
          className="pointer-events-none fixed z-[301] rounded-2xl border-2 border-emerald-300 shadow-[0_0_0_9999px_rgba(2,6,23,0.76),0_0_0_5px_rgba(52,211,153,0.25),0_0_35px_rgba(52,211,153,0.5)]"
          style={highlight}
        />
      )}
      <section
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="spotlight-title"
        className="fixed z-[302] max-h-[calc(100vh-1.5rem)] overflow-y-auto rounded-[1.6rem] border border-white/20 bg-white p-5 shadow-[0_28px_85px_rgba(2,6,23,0.4)] sm:p-6"
        style={{ top: cardTop, left: cardLeft, width: cardWidth }}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700">
            <Sparkles className="h-3.5 w-3.5" /> {label}
          </span>
          <button type="button" onClick={onFinish} aria-label="Fechar guia" className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-800"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-4 flex items-center gap-2">
          {steps.map((item, index) => <span key={item.target} className={`h-1.5 flex-1 rounded-full ${index <= stepIndex ? 'bg-emerald-500' : 'bg-slate-200'}`} />)}
        </div>
        <p className="mt-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Etapa {stepIndex + 1} de {steps.length}</p>
        <h2 id="spotlight-title" className="mt-1 text-xl font-black leading-tight text-slate-950">{step.title}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">{step.description}</p>
        {step.detail && <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold leading-5 text-emerald-800">{step.detail}</p>}
        <div className="mt-5 flex items-center justify-between gap-2 border-t border-slate-100 pt-4">
          <button type="button" onClick={onFinish} className="text-xs font-bold text-slate-500 hover:text-slate-900">Pular guia</button>
          <div className="flex gap-2">
            {stepIndex > 0 && <button type="button" onClick={() => onStepChange(stepIndex - 1)} className="flex h-10 items-center gap-1 rounded-xl border border-slate-200 px-3 text-xs font-bold text-slate-700"><ArrowLeft className="h-4 w-4" /> Voltar</button>}
            <button ref={nextButtonRef} type="button" onClick={() => stepIndex === steps.length - 1 ? onFinish() : onStepChange(stepIndex + 1)} className="flex h-10 items-center gap-1 rounded-xl bg-slate-950 px-4 text-xs font-black text-white hover:bg-slate-800">
              {stepIndex === steps.length - 1 ? 'Concluir' : 'Próximo'} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>
    </div>,
    document.body
  );
};
